import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import Toast from "../Toast/Toast";
import { Star } from "lucide-react";

const CloneEmailTemplateModal = ({ isOpen, onClose, templates, onSubmit }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [loading, setLoading] = useState(false);

  const steps = [
    {
      id: 1,
      name: `${t("settings:select_template")}`,
      status: step >= 1 ? "current" : "upcoming",
    },
    {
      id: 2,
      name: `${t("settings:configure")}`,
      status: step === 2 ? "current" : step > 2 ? "complete" : "upcoming",
    },
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setStep(2);
    setEmailSubject(template.email_subject);
    setTemplateName(`${template.template_name} - ${t("settings:copy")}`);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:template_name_required"),
      });
      return;
    }

    if (!emailSubject.trim()) {
      Toast({
        type: "error",
        title: t("settings:error"),
        message: t("settings:email_subject_required"),
      });
      return;
    }

    setLoading(true);
    try {
      await onSubmit(selectedTemplate.template_id, templateName, emailSubject);
      setStep(1);
      setSelectedTemplate(null);
      setTemplateName("");
      setEmailSubject("");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedTemplate(null);
    setTemplateName("");
    setEmailSubject("");
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="clone-email-template-modal"
                className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex flex-col">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-gray-900"
                      >
                        {step === 1
                          ? t("settings:clone_template")
                          : t("settings:modify_template")}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500 mb-4">
                        {t("settings:select_template_clone")}
                      </p>
                    </div>
                    <button
                      type="button"
                      id="clone-email-template-modal-close"
                      className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-200"
                      onClick={handleClose}
                    >
                      <span className="sr-only">{t("settings:close")}</span>
                      <div className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                        &#10005;
                      </div>
                    </button>
                  </div>

                  {/* Multi-step progress indicator */}
                  <div className="mt-6 mb-8 px-4">
                    <div className="relative flex items-center justify-between">
                      <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-gray-300" />
                      <div
                        className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-blue-500 transition-all duration-500 ease-out"
                        style={{ width: step === 1 ? "50%" : "100%" }}
                      />
                      {steps.map((stepItem, index) => (
                        <div
                          key={stepItem.id}
                          className="relative z-10 flex items-center justify-center"
                          style={{
                            marginLeft: index === 0 ? "0" : "auto",
                            marginRight:
                              index === steps.length - 1 ? "0" : "auto",
                          }}
                        >
                          <div
                            className={`w-3 h-3 rounded-full transition-all duration-500 ease-out ${
                              step >= stepItem.id
                                ? "bg-blue-500 scale-100"
                                : "bg-gray-300 scale-100"
                            }`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2">
                    {step === 1 ? (
                      <div className="space-y-4">
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {templates.map((template) => (
                            <button
                              key={template.template_id}
                              type="button"
                              id={`clone-email-template-modal-template-${template.template_id}`}
                              onClick={() => handleTemplateSelect(template)}
                              className="w-full text-left p-4 border rounded-lg hover:bg-blue-50 transition-colors focus:outline-none duration-500"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-gray-900">
                                    {template.template_name}
                                  </h4>
                                  <p className="text-sm text-gray-500 mt-1">
                                    {template.email_subject}
                                  </p>
                                  {template.template_description && (
                                    <p className="text-sm text-gray-400 mt-1">
                                      {template.template_description}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col items-end">
                                  {template.is_new && (
                                    <div className="flex justify-center items-center gap-1 text-center ml-2 px-2 py-1 text-xs bg-cfActiveBg text-cfActiveText rounded-full">
                                      <Star
                                        size={15}
                                        className="inline-block"
                                      />
                                      <span>{t("settings:new")}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div>
                          <label
                            htmlFor="templateName"
                            className="block text-sm font-medium text-gray-700 mb-2 after:content-['*'] after:text-red-500"
                          >
                            {t("settings:template_name")} &nbsp;
                          </label>
                          <input
                            type="text"
                            id="clone-email-template-modal-input-name"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            className="w-full p-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
                            placeholder={t("settings:enter_template_name")}
                          />
                        </div>

                        <div>
                          <label
                            htmlFor="emailSubject"
                            className="block text-sm font-medium text-gray-700 mb-2 after:content-['*'] after:text-red-500"
                          >
                            {t("settings:email_subject")} &nbsp;
                          </label>
                          <input
                            type="text"
                            id="clone-email-template-modal-input-subject"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            className="w-full p-2 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
                            placeholder={`${t("settings:enter_email_subject")}`}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {step === 2 && (
                    <div className="mt-6 flex justify-between items-center">
                      <button
                        type="button"
                        id="clone-email-template-modal-back"
                        onClick={handleBack}
                        className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                      >
                        {t("settings:back")}
                      </button>

                      <button
                        type="submit"
                        id="clone-email-template-modal-submit"
                        disabled={
                          !templateName.trim() ||
                          !emailSubject.trim() ||
                          loading
                        }
                        className={`px-6 py-2.5 text-sm font-medium text-cfSecondary rounded-[8px] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
                          loading
                            ? "bg-blue-400 cursor-not-allowed"
                            : !templateName.trim() || !emailSubject.trim()
                              ? "bg-blue-300 cursor-not-allowed"
                              : "bg-cfPrimary hover:bg-blue-700 hover:shadow-md transform hover:-translate-y-0.5"
                        }`}
                      >
                        {loading ? (
                          <span className="flex items-center">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-cfSecondary"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {t("settings:cloning")}
                          </span>
                        ) : (
                          `${t("settings:clone_template")}`
                        )}
                      </button>
                    </div>
                  )}
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CloneEmailTemplateModal;
